import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';

type InputType = 'text' | 'number' | 'tel' | 'email' | 'password' | 'search' | 'url';

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'type'> {
  type?: InputType;
  label?: string;
  helper?: ReactNode;
  error?: string;
  className?: string;
  inputClassName?: string;
}

// text-base = 1rem = 16px (Tailwind v4 default base size). The 16px floor
// is what prevents iOS Safari from auto-zooming the viewport on focus.
// Do NOT downsize the input text below text-base, even on dense layouts —
// pre-zoom UX cost there is much smaller than a viewport jump cost.
export const Input = forwardRef<HTMLInputElement, InputProps>(
  function Input(
    {
      type = 'text',
      label,
      helper,
      error,
      className = '',
      inputClassName = '',
      id: idProp,
      required,
      ...rest
    },
    ref,
  ) {
    const generatedId = useId();
    const id = idProp ?? generatedId;
    const helperId = `${id}-helper`;
    const errorId = `${id}-error`;
    const describedBy = error ? errorId : helper ? helperId : undefined;

    return (
      <div className={`flex flex-col gap-1.5 ${className}`}>
        {label && (
          <label
            htmlFor={id}
            className="text-[11px] font-bold uppercase tracking-widest text-coal"
          >
            {label}
            {required && (
              <span className="text-danger ml-1" aria-hidden="true">
                *
              </span>
            )}
          </label>
        )}
        <input
          ref={ref}
          id={id}
          type={type}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={`h-11 px-3 rounded-xl border bg-white text-base text-ink placeholder:text-metal outline-none transition-colors focus:border-teal focus:ring-2 focus:ring-teal/20 disabled:opacity-50 disabled:bg-ghost disabled:cursor-not-allowed ${error ? 'border-danger' : 'border-stone hover:border-coal/40'} ${inputClassName}`}
          {...rest}
        />
        {error ? (
          <p id={errorId} className="text-xs text-danger font-medium">
            {error}
          </p>
        ) : helper ? (
          <p id={helperId} className="text-xs text-coal">
            {helper}
          </p>
        ) : null}
      </div>
    );
  },
);
